import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, ReplaySubject, Subject, throwError } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ContractOverviewModel, GenericPostRequest, PaginationPage, PaginationResponse } from 'src/app/classes';
import { serverLocation } from 'src/app/globals';
import { TokenService } from '../../shared/services/token.service';
import { Contract, ContractItem, ContractStatus, ContractType } from '../classes';

@Injectable({
  providedIn: 'root'
})
export class ContractService {

  private _contracts: { [key: number]: ReplaySubject<Contract> } = {};
  private _customerContracts: { [key: number]: ReplaySubject<Contract[]> } = {};

  public contracts$ = combineLatest(Object.values(this._contracts));

  private _allContractStatusLoaded = new BehaviorSubject(false);
  private _contractStatus: { [key: number]: ReplaySubject<ContractStatus> } = {};

  private _allContractTypesLoaded = new BehaviorSubject(false);
  private _contractTypes: { [key: number]: ReplaySubject<ContractType> } = {};

  constructor(
    private _httpClient: HttpClient,
    private _tokenService: TokenService
  ) { }

  checkContractVersion(contract: Contract): Observable<boolean> {
    return this._httpClient.get(`${serverLocation}/api/contract/${contract.contractid}/check-version/${contract.concurrencystamp}`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<boolean>;
  }

  clearContractHistory = () => this._httpClient.delete(`${serverLocation}/api/contract/history`, {
    headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
  });

  contractIsWatched(contractId: number): Observable<boolean> {
    return this._httpClient.get(`${serverLocation}/api/contract/${contractId}/watched`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<boolean>;
  }

  createContract(contract: Contract, note: string = null): Observable<Contract> {
    return this._httpClient.post(`${serverLocation}/api/contract`, {
      content: contract,
      note: note
    }, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<Contract>;
  }

  createContractItem(contractItem: ContractItem, note: string = null): Observable<ContractItem> {
    return this._httpClient.post(`${serverLocation}/api/contract-item`, {
      content: contractItem,
      note: note
    }, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<ContractItem>;
  }

  deleteContract(contractId: number): Observable<Object> {
    let subject = new Subject();
    this._deleteContractById(contractId).pipe(tap(() => {
      if (this._contracts[contractId]) {
        this._contracts[contractId].complete();
        delete this._contracts[contractId];
      }
    })).subscribe(subject);
    return subject;
  }

  deleteContractItem(identifier: number): Observable<Object> {
    return this._httpClient.delete(`${serverLocation}/api/contract-item/${identifier}`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<ContractItem>;
  }

  generateContractNumber(): Observable<number> {
    return this._httpClient.get(`${serverLocation}/api/contract/generate-number`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<number>;
  }

  getContractById(identifier: number, soft: boolean = true): Observable<Contract> {
    if (typeof identifier !== 'number') return throwError('no valid contract identifier passed');
    if (!soft || !this._contracts[identifier]) {
      if (!this._contracts[identifier]) this._contracts[identifier] = new ReplaySubject(1);
      this._getContractById(identifier).subscribe({
        next: (contract: Contract) => this._contracts[identifier].next(contract),
        error: (error: HttpErrorResponse) => this._contracts[identifier].error(error)
      });
    }
    return this._contracts[identifier].asObservable();
  }

  getContractStatusById(id: number, soft: boolean = true): Observable<ContractStatus> {
    if (!this._contractStatus[id]) {
      this._contractStatus[id] = new ReplaySubject<ContractStatus>(1);
      soft = false;
    }
    if (!soft) this._getContractStatusById(id).subscribe({
      next: (status) => this._contractStatus[id].next(status),
      error: (error) => this._contractStatus[id].error(error)
    });
    return this._contractStatus[id].asObservable();
  }

  getContractTypeById(id: number, soft: boolean = true): Observable<ContractType> {
    if (!this._contractTypes[id]) {
      this._contractTypes[id] = new ReplaySubject<ContractType>(1);
      soft = false;
    }
    if (!soft) this._getContractTypeById(id).subscribe({
      next: (type) => this._contractTypes[id].next(type),
      error: (error) => this._contractTypes[id].error(error)
    });
    return this._contractTypes[id].asObservable();
  }

  getCustomerContracts(identifier: number, soft: boolean = true): Observable<Contract[]> {
    if (typeof identifier !== 'number') return throwError('no valid customer identifier passed');
    if (!soft || !this._customerContracts[identifier]) {
      if (!this._customerContracts[identifier]) this._customerContracts[identifier] = new ReplaySubject(1);
      this._getCustomerContracts(identifier).subscribe({
        next: (contracts: Contract[]) => {
          for (let contract of contracts) {
            if (!this._contracts[contract.contractid]) this._contracts[contract.contractid] = new ReplaySubject(1);
            this._contracts[contract.contractid].next(contract);
          }
          this._customerContracts[identifier].next(contracts);
        },
        error: (error: HttpErrorResponse) => this._customerContracts[identifier].error(error)
      });
    }
    return this._customerContracts[identifier].asObservable();
  }

  getUserContractHistory(page: PaginationPage) {
    return this._httpClient.post(`${serverLocation}/api/contract/history`, page, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<PaginationResponse<ContractOverviewModel[]>>;
  }

  getUserContractFavourites(page: PaginationPage) {
    return this._httpClient.post(`${serverLocation}/api/contract/favourites`, page, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<PaginationResponse<ContractOverviewModel[]>>;
  }

  loadAllContractStatus(soft: boolean = true): Observable<ContractStatus[]> {
    if (this._allContractStatusLoaded.value && soft) return combineLatest(Object.values(this._contractStatus));
    return this._getAllContractStatus().pipe(tap((contractStatus: ContractStatus[]) => {
      for (let status of contractStatus) {
        if (typeof this._contractStatus[status.contractstateid] === 'undefined') this._contractStatus[status.contractstateid] = new ReplaySubject<ContractStatus>(1);
        this._contractStatus[status.contractstateid].next(status);
      }
      this._allContractStatusLoaded.next(true);
    }));
  }

  loadAllContractTypes(soft: boolean = true): Observable<ContractType[]> {
    if (this._allContractStatusLoaded.value && soft) return combineLatest(Object.values(this._contractTypes));
    return this._getAllContractTypes().pipe(tap((contractTypes: ContractType[]) => {
      for (let contractType of contractTypes) {
        if (typeof this._contractTypes[contractType.contracttypeid] === 'undefined') this._contractTypes[contractType.contracttypeid] = new ReplaySubject<ContractType>(1);
        this._contractTypes[contractType.contracttypeid].next(contractType);
      }
      this._allContractTypesLoaded.next(true);
    }));
  }

  saveHistory(customerId: number): Observable<Object> {
    let subject = new Subject();
    this._httpClient.get(`${serverLocation}/api/contract/history/${customerId}`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }).subscribe(subject);
    return subject.asObservable();
  }

  searchContractsByString(search: string, paginationPage: PaginationPage = { pageNumber: 1, pageSize: 20 }) {
    return this._httpClient.post(`${serverLocation}/api/contract/search`, {
      'searchString': search,
      'page': paginationPage
    }, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<PaginationResponse<ContractOverviewModel[]>>;
  }

  searchContractsByUserFilter(identifier: number, paginationPage: PaginationPage = { pageNumber: 1, pageSize: 20 }) {
    return this._httpClient.post(`${serverLocation}/api/contract/user-filter-search/${identifier}`, paginationPage, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<PaginationResponse<ContractOverviewModel[]>>;
  }

  setContractWatchedStatus(contactId: number, watch: boolean): Observable<boolean> {
    return this._httpClient.get(`${serverLocation}/api/contract/${contactId}/${watch? 'watch': 'unwatch'}`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<boolean>;
  }

  updateContract(contract: Contract, note: string = null): Observable<Contract> {
    return this._httpClient.put(`${serverLocation}/api/contract`, {
      content: contract,
      note: note
    } as GenericPostRequest<Contract>, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<Contract>;
  }

  updateContractItem(contractItem: ContractItem, note: string = null): Observable<ContractItem> {
    return this._httpClient.put(`${serverLocation}/api/contract-item`, {
      content: contractItem,
      note: note
    }, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<ContractItem>;
  }

  private _deleteContractById(contractId: number): Observable<Object> {
    return this._httpClient.delete(`${serverLocation}/api/contract/${contractId}`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<Object>;
  }

  private _getContractById(identifier: number): Observable<Contract> {
    return this._httpClient.get(`${serverLocation}/api/contract/${identifier}`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<Contract>;
  }

  private _getCustomerContracts(identifier: number): Observable<Contract[]> {
    return this._httpClient.get(`${serverLocation}/api/contract/by-customer/${identifier}`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<Contract[]>;
  }

  private _getContractStatusById = (statusId: number): Observable<ContractStatus> => this._httpClient.get(`${serverLocation}/api/contract-status/${statusId}`, {
    headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
  }) as Observable<ContractStatus>;

  private _getContractTypeById = (typeId: number): Observable<ContractType> => this._httpClient.get(`${serverLocation}/api/contract-type/${typeId}`, {
    headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
  }) as Observable<ContractType>;

  private _getAllContractStatus = (): Observable<ContractStatus[]> => this._httpClient.get(`${serverLocation}/api/contract-status`, {
    headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
  }) as Observable<ContractStatus[]>;

  private _getAllContractTypes = (): Observable<ContractType[]> => this._httpClient.get(`${serverLocation}/api/contract-type`, {
    headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
  }) as Observable<ContractType[]>;
}
